import React, { useEffect, useState } from "react";
import AdminLogTable from "./AdminLogTable";
import userServices from "services/userServices";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import ExportModal from "./ExportModal";

function AdminLogs({ userId = null }) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [adminLogs, setAdminLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [entityFilter, setEntityFilter] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [tableState, setTableState] = useState({
    page: 1,
    limit: 20,
    sortBy: "createdAt",
    sortOrder: -1,
    search: "",
  });

  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
  });

  const fetchAdminLogs = async () => {
    setIsLoading(true);
    try {
      const res = await userServices.getActivityLogs({
        userId,
        page: tableState.page,
        limit: tableState.limit,
        sortBy: tableState.sortBy,
        sortOrder: tableState.sortOrder,
        search: tableState.search,
        entity: entityFilter,
      });

      if (res?.success) {
        const logs = res?.adminLogs || res?.data?.adminLogs || [];
        const metaData = res?.meta?.admin || res?.meta || res?.data?.meta || { totalItems: 0, totalPages: 0 };
        setAdminLogs(logs);
        setMeta(metaData);
      } else {
        notify("error", t("page.adminLogs.toast.error.fetchFailed", "Failed to fetch admin logs"));
      }
    } catch (error) {
      console.error("Error fetching admin logs:", error);
      notify("error", t("page.adminLogs.toast.error.fetchFailed", "Failed to fetch admin logs"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminLogs();
    // eslint-disable-next-line
  }, [
    tableState.page,
    tableState.limit,
    tableState.sortBy,
    tableState.sortOrder,
    tableState.search,
    entityFilter,
    userId,
  ]);

   const handleExport = async (exportParams) => {
    try {
      setIsExporting(true);
      const params = {
        ...exportParams,
        search: tableState.search,
        entity: entityFilter,
        userId,
      };

      const res = await userServices.exportAdminLogs(params);

      if (!res?.success) {
        notify("error", "Export failed");
        return;
      }

      // Determine the MIME type
      const mimeType =
        exportParams.format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv";

      // Create blob with proper MIME
      const blob = new Blob([res.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        exportParams.format === "excel" ? "admin_logs.xlsx" : "admin_logs.csv"
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      notify("error", "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <AdminLogTable
        logs={adminLogs}
        isLoading={isLoading}
        entityFilter={entityFilter}
        setEntityFilter={setEntityFilter}
        tableState={tableState}
        setTableState={setTableState}
        meta={meta}
        setShowExportModal={setShowExportModal}
      />

       <ExportModal
        isOpen={showExportModal}
        toggle={() => setShowExportModal(false)}
        onExport={handleExport}
        loading={isExporting}
      />
    </>
  );
}

export default AdminLogs;